/**
 * camps.price_unit_source — the m526 WITNESS the render gate reads (#2294 B+C).
 *
 * `observedPriceUnit` (lib/price.ts) prints a stored period only when this column
 * names a trusted producer. This module owns the witness end to end:
 *
 *   * the PURE stamp decision for BPRD camps (`decidePriceUnitWitnessStamp`) —
 *     fixture-reachable, per lib/camps/price-rollup-range.ts's rule that price
 *     decisions live where a node test can break them (its IO wrapper is
 *     `stampBprdDerivedPriceUnitWitness` in lib/camps/price-rollup.ts);
 *   * the deploy-window-tolerant batch reader the render loaders use
 *     (`loadCampPriceUnitWitnesses`).
 *
 * DEPLOY-AHEAD WINDOW (m526). Code deploys before a human applies the migration, so
 * both the reads and the write here classify the missing-column error and degrade:
 * reads resolve every witness to null (the gate then prints no period — exactly the
 * pre-m526 rendered state for published camps), and the stamp skips with one warn.
 * The pattern is lib/discovery/research-bridge/safe-write.ts's, specialized to the
 * one column this module owns.
 *
 * NO IMPORTS AT ALL, deliberately: the pure decision must be importable by
 * contract tests, and the batch reader is called from both server loaders and the
 * saved-drawer client component, so nothing may drag the scraper graph into a
 * client bundle. The IO half of the BPRD stamp lives in lib/camps/price-rollup.ts
 * (server-side, next to the rollup that calls it); this module holds its decision.
 * Nothing here touches parent-owned tables.
 */

/** The m526 CHECK vocabulary. NULL (unwitnessed) is deliberately not in it. */
export const PRICE_UNIT_WITNESS_DERIVED = "derived_iso_week" as const;
export const PRICE_UNIT_WITNESS_EXTRACTED = "extracted" as const;

const M526_HINT =
  "apply supabase/migrations/20260901100000_m526_camps_price_unit_witness.sql";

/**
 * Is this error PostgREST/Postgres saying camps.price_unit_source does not exist
 * yet — i.e. genuinely the pre-m526 deploy window, not some other failure?
 *
 * 42703 = undefined_column (reads); PGRST204 = column missing from the schema
 * cache (writes). Both must NAME the column: a 42703 about some other column is a
 * real defect and must not be silently absorbed as "migration pending".
 */
export function isMissingPriceUnitSourceColumn(
  error: { code?: string; message?: string } | null,
): boolean {
  if (!error) return false;
  const named = (error.message ?? "").includes("price_unit_source");
  if (!named) return false;
  if (error.code === "42703" || error.code === "PGRST204") return true;
  // PostgREST does not always surface a code on schema-cache misses; the two
  // documented message shapes are "does not exist" and "Could not find the … column".
  const text = (error.message ?? "").toLowerCase();
  return text.includes("does not exist") || text.includes("could not find");
}

/**
 * The BPRD post-apply stamp decision, pure (#2294 C).
 *
 * `derivedUnit` is deriveIsoWeekPriceUnitFromWeeks over the camp_weeks the port just
 * wrote — the SAME one-ISO-week test that produced the stored unit, re-run against
 * the current evidence. Three outcomes:
 *
 *   1. The stored unit REPRODUCES (derivedUnit === storedUnit, non-null): it is the
 *      derivation's output, so it carries the 'derived_iso_week' witness. No write
 *      when the witness is already there — the daily rollup must not churn the row.
 *   2. It does NOT reproduce and the current witness is OURS ('derived_iso_week'):
 *      the witness describes evidence that has changed — clear it. This is the
 *      staleness contract on the m526 column: a witness left behind by a unit or
 *      week rewrite must not keep printing the period.
 *   3. It does not reproduce and the witness is FOREIGN ('extracted') or absent:
 *      no write. This stamp manages only the witness class it mints, so a future
 *      non-BPRD caller of the rollup cannot strip an extraction's witness.
 *
 * Returns the one-column patch to write, or null for "no write".
 */
export function decidePriceUnitWitnessStamp(input: {
  storedUnit: string | null;
  storedWitness: string | null;
  derivedUnit: "per_week" | "total" | null;
}): { price_unit_source: typeof PRICE_UNIT_WITNESS_DERIVED | null } | null {
  const { storedUnit, storedWitness, derivedUnit } = input;
  if (storedUnit !== null && derivedUnit === storedUnit) {
    return storedWitness === PRICE_UNIT_WITNESS_DERIVED
      ? null
      : { price_unit_source: PRICE_UNIT_WITNESS_DERIVED };
  }
  if (storedWitness === PRICE_UNIT_WITNESS_DERIVED) {
    return { price_unit_source: null };
  }
  return null;
}

/**
 * Minimal structural Supabase client — the factories carry no Database generic
 * (see lib/providers/public-email-read.ts for the same shape and the reason).
 */
type WitnessClient = {
  from: (table: never) => unknown;
};

type SelectResult = Promise<{
  data: unknown;
  error: { code?: string; message?: string } | null;
}>;

function tableOf(client: WitnessClient, table: string): {
  select: (cols: string) => {
    in: (col: string, values: string[]) => SelectResult;
  };
} {
  return (client.from as unknown as (t: string) => ReturnType<typeof tableOf>)(table);
}

/**
 * The deploy-window warn, shared by the reader here and the stamp in
 * lib/camps/price-rollup.ts so the operator hint always names the migration.
 */
export function warnPriceUnitWitnessColumnMissing(context: string): void {
  console.warn(
    `[price-unit-witness] camps.price_unit_source not in the schema yet — ${context} (${M526_HINT})`,
  );
}

/** `.in()` chunk size — mirrors the repo-wide chunking discipline for id lists. */
const WITNESS_READ_CHUNK = 100;

/**
 * Read the m526 witness for a batch of camps, tolerantly.
 *
 * Render loaders whose main query is an explicit column list CANNOT add
 * `price_unit_source` to it — an unknown column fails the WHOLE select (42703)
 * until m526 is applied, which would take the page down for the deploy window.
 * They call this instead: a second, single-purpose read that degrades to "no
 * witness" (the gate then prints no period — the safe direction) on every
 * failure, with one warn naming the migration when the failure IS the window.
 *
 * Ids absent from the result mean null witness; callers can `?? null` freely.
 */
export async function loadCampPriceUnitWitnesses(
  client: WitnessClient,
  campIds: ReadonlyArray<string | null | undefined>,
): Promise<Map<string, string | null>> {
  const out = new Map<string, string | null>();
  const ids = [...new Set(campIds.filter((id): id is string => typeof id === "string" && id.length > 0))];
  if (ids.length === 0) return out;

  for (let i = 0; i < ids.length; i += WITNESS_READ_CHUNK) {
    const chunk = ids.slice(i, i + WITNESS_READ_CHUNK);
    const { data, error } = await tableOf(client, "camps")
      .select("id, price_unit_source")
      .in("id", chunk);
    if (error) {
      if (isMissingPriceUnitSourceColumn(error)) {
        warnPriceUnitWitnessColumnMissing("rendering without periods");
        return out;
      }
      console.warn(
        `[price-unit-witness] witness read failed (${chunk.length} camps): ${error.message}`,
      );
      continue;
    }
    for (const raw of (Array.isArray(data) ? data : []) as Array<Record<string, unknown>>) {
      if (typeof raw.id !== "string") continue;
      out.set(raw.id, typeof raw.price_unit_source === "string" ? raw.price_unit_source : null);
    }
  }
  return out;
}
